import { useEffect, useId, useRef, useState } from 'react';
import { API_BASE } from '../client/config';
import { Button } from './ui';
import { ModalShell } from './menu';
import { useWriteGate } from '../nav/useWriteGate';
import { ROLE_LABELS as ORG_ROLE_LABELS } from '../labels/orgConcepts';

// =============================================================================
// 담당 그룹 편집 — 고객사 관리(BO)의 관리자 행 '담당 그룹' 버튼이 여는 모달.
//
// 2026-09-17 대교: "고객사의 관리자를 추가한 후 담당 그룹을 지정할 때 담당 그룹만 선택하면 될 것을
// 권한을 지정하게 되어 있음. 권한 지정 삭제."
//   · 관리자를 만들 때 이미 권한(조직 관리자/그룹 관리자/편집자/조회자)을 한 번 고른다. 여기서 그룹마다
//     또 역할을 고르게 하면 같은 사람에게 권한을 두 번 묻는 꼴이고, 두 값이 어긋날 수 있었다.
//     실제로 체크를 새로 켜면 역할이 무조건 'Viewer'로 들어가서, 편집자를 그룹에 넣어도 그 그룹에서는
//     읽기만 되는 상태가 만들어졌다(서버 permits()는 조직 역할 ∧ 그룹 역할을 둘 다 본다).
//   · 그래서 이 화면은 '어느 그룹을 맡는가'만 고르고, 그룹 안 역할은 그 사람의 조직 권한을 그대로 보낸다.
//     구성 관리(GroupWorkspace·DeviceGroups)가 2026-09-15에 이미 택한 방식과 같아졌다.
//
// 서버 계약은 그대로 둔다(PUT /api/device-groups/members/:userId 은 계속 {groupId, role}[] 을 받는다).
// role 을 옵션으로 만들면 repo·라우트·seed·테스트 수십 곳이 함께 흔들리고, 무엇보다 resource_grants.role 이
// 비면 permits()·resolve()의 권한 판정이 무너진다. 화면이 조직 역할을 채워 보내는 쪽이 훨씬 덜 위험하다.
//
// 기존 데이터: 예전에 그룹마다 다른(조직 권한보다 낮은) 역할이 들어간 행이 있을 수 있다. 마이그레이션은
// 하지 않는다. 읽을 때는 role 을 아예 보지 않고 '이 그룹을 맡는다/아니다'로만 읽고, 다음에 저장하는
// 순간 조직 권한으로 맞춰진다. 서버 validateMemberAssignment 가 그룹 역할이 조직 역할보다 셀 수 없게
// 막고 있으므로, 남아 있는 옛 값은 항상 '조직 권한 이하'라 안전한 쪽으로만 틀린다.
// =============================================================================

export type GroupAssignmentRole = 'Admin' | 'Editor' | 'Viewer';
export type GroupMemberAssignments = {
  userId: string;
  role: string;
  central: boolean;
  assignments: Array<{ groupId: string; role: GroupAssignmentRole }>;
  /** 신원. 조직 Owner는 /api/orgs/current/detail 로도 받지만, 그룹 관리자는 그 API를 못 부른다
   *  (org:manage). 구성 관리의 관리자 표가 한 곳에서 다 받을 수 있게 목록 API가 같이 싣는다. */
  name?: string | null;
  loginId?: string | null;
  email?: string;
  lastSeenAt?: string | null;
};
export type GroupMemberOptions = {
  groups: Array<{ id: string; name: string }>;
  actor: { orgId: string; role: string | null; central: boolean };
};

const ROLES: GroupAssignmentRole[] = ['Admin', 'Editor', 'Viewer'];
/** 조직 권한을 그대로 그룹 역할로 쓴다. Owner는 애초에 그룹 배정 대상이 아니다(항상 전체 그룹). */
export function groupRoleOf(orgRole: string): GroupAssignmentRole | null {
  return ROLES.includes(orgRole as GroupAssignmentRole) ? orgRole as GroupAssignmentRole : null;
}

export function GroupMemberEditor({ orgId, member, onClose, onSaved }: {
  orgId: string;
  member: { userId: string; name: string | null; role: string };
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [options, setOptions] = useState<GroupMemberOptions | null>(null);
  /** 담당 그룹 id만 담는다 — 그룹별 역할은 더 이상 사람이 고르지 않는다(2026-09-17). */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [central, setCentral] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const mounted = useRef(false);
  const descriptionId = useId();
  // 대행 '읽기 전용'이면 저장을 막되 버튼은 자리를 지킨다(검수 S1).
  const write = useWriteGate();
  const memberUrl = `${API_BASE}/api/device-groups/members/${encodeURIComponent(member.userId)}?orgId=${encodeURIComponent(orgId)}`;

  useEffect(() => {
    const controller = new AbortController();
    mounted.current = true;
    setLoading(true);
    setOptions(null);
    setError(null);
    void Promise.all([
      fetch(`${API_BASE}/api/device-groups?orgId=${encodeURIComponent(orgId)}`, { credentials: 'include', signal: controller.signal }),
      fetch(memberUrl, { credentials: 'include', signal: controller.signal }),
    ]).then(async (responses) => {
      const bodies = await Promise.all(responses.map(async (response) => {
        const body = await response.json();
        if (!response.ok) throw new Error(body.error || `조회 실패 (HTTP ${response.status})`);
        return body;
      }));
      if (controller.signal.aborted) return;
      const choices = bodies[0] as GroupMemberOptions;
      if (choices.actor.orgId !== orgId) throw new Error('조직 정보를 다시 확인해 주세요.');
      const current = bodies[1] as Pick<GroupMemberAssignments, 'assignments' | 'central'>;
      setOptions(choices);
      // 저장된 그룹 역할은 읽지 않는다 — '어느 그룹을 맡는가'만 가져온다.
      setPicked(new Set(current.assignments.map((assignment) => assignment.groupId)));
      setCentral(current.central);
    }).catch((reason: unknown) => {
      if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : String(reason));
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => { mounted.current = false; controller.abort(); };
  }, [orgId, memberUrl, reload]);

  const owner = member.role === 'Owner';
  const groupRole = groupRoleOf(member.role);
  const canEdit = !!options?.actor.central && options.actor.role === 'Owner' && !owner && !!groupRole;
  const invalidCentral = central && member.role !== 'Admin';
  const close = () => { if (!pending) onClose(); };
  const toggle = (groupId: string, on: boolean) => setPicked((current) => {
    const next = new Set(current);
    if (on) next.add(groupId); else next.delete(groupId);
    return next;
  });

  async function save(): Promise<void> {
    if (loading || pending || !canEdit || !groupRole || invalidCentral || write.blocked) return;
    setPending(true);
    setError(null);
    try {
      const response = await fetch(memberUrl, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        // 서버 계약은 그대로 {groupId, role}[] — role 은 사람이 고른 값이 아니라 이 사람의 조직 권한이다.
        body: JSON.stringify({ assignments: [...picked].map((groupId) => ({ groupId, role: groupRole })), central }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null) as { error?: string } | null;
        throw new Error(body?.error || `저장 실패 (HTTP ${response.status})`);
      }
      if (mounted.current) onSaved();
    } catch (reason) {
      if (mounted.current) setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      if (mounted.current) setPending(false);
    }
  }

  // 껍데기(role=dialog·포커스 트랩·Esc·opener 복귀)와 header/body(스크롤)/footer(고정) 3분할은
  // ModalShell이 맡는다(2026-09-16 검수 P2·S3). 예전엔 패널 전체가 스크롤이라 '담당 그룹 저장'이
  // 목록 아래로 밀려 내려갔다.
  return (
    <ModalShell title={`담당 그룹 · ${member.name ?? '계정'}`} size="md" onClose={close}
      footer={<>
        <Button disabled={pending} onClick={close}>취소</Button>
        {!owner && <Button variant="primary" {...write.props}
          disabled={loading || pending || !canEdit || invalidCentral || write.blocked}
          onClick={() => void save()}>{pending ? '저장 중…' : '담당 그룹 저장'}</Button>}
      </>}>
      <div className="flex min-w-0 flex-col gap-4" aria-busy={loading || pending}>
        <p id={descriptionId} className="text-[14px] text-text-dim">
          권한: {ORG_ROLE_LABELS[member.role as keyof typeof ORG_ROLE_LABELS] ?? member.role}. 담당 그룹은 여러 개 고를 수 있고, 각 그룹에서는 이 권한 그대로 활동합니다. 권한을 바꾸려면 관리자 수정에서 바꾸세요.
        </p>
        {error && <div role="alert" className="rounded border border-red/50 bg-red/15 px-3 py-2 text-[14px] text-red">{error}</div>}
        {loading ? <p role="status">담당 그룹을 불러오는 중…</p> : !options ? (
          <Button onClick={() => setReload((value) => value + 1)}>다시 불러오기</Button>
        ) : owner ? (
          <p className="text-[14px]">Owner는 항상 전체 그룹을 관리합니다. 별도 그룹 배정은 필요하지 않습니다.</p>
        ) : (
          <>
            {!canEdit && <p role="status" className="text-[14px] text-text-dim">담당 그룹 변경은 조직 Owner만 할 수 있습니다.</p>}
            {member.role === 'Admin' && options.actor.role === 'Owner' && options.actor.central && (
              <label className="flex items-center gap-2 rounded border border-line bg-panel-2 p-3 text-[14px]">
                <input type="checkbox" checked={central} disabled={pending} onChange={(event) => setCentral(event.target.checked)} />
                중앙 관리자 · 전체 그룹 관리
              </label>
            )}
            {central && <p className="text-[13px] text-text-dim">중앙 권한은 전체 그룹에 적용됩니다. 아래 배정은 중앙 권한을 해제한 뒤 적용됩니다.</p>}
            {invalidCentral && <p role="alert" className="text-[14px] text-red">중앙 관리자는 ‘그룹 관리자’ 권한에만 지정할 수 있습니다. 체크를 해제해 주세요.</p>}
            <fieldset disabled={pending || !canEdit} className="min-w-0">
              <legend className="mb-2 text-[14px] font-semibold">담당 그룹 선택</legend>
              <div className="space-y-2">
                {options.groups.map((group) => (
                  <label key={group.id} className="flex min-w-0 items-center gap-2 rounded border border-line bg-panel-2 px-3 py-2 text-[14px]">
                    <input type="checkbox" checked={picked.has(group.id)} onChange={(event) => toggle(group.id, event.target.checked)} />
                    <span className="min-w-0 flex-1 truncate">{group.name}</span>
                  </label>
                ))}
                {/* 목록에 없는(조회 권한 밖·보관된) 그룹에 남은 배정. 저장 때 그대로 유지되므로 빼고 싶을 때만 체크를 푼다. */}
                {[...picked].filter((groupId) => !options.groups.some((group) => group.id === groupId)).map((groupId) => (
                  <label key={groupId} className="flex items-center gap-2 text-[14px] text-text-dim">
                    <input type="checkbox" checked onChange={() => toggle(groupId, false)} />
                    조회할 수 없는 그룹 · 배정 해제 가능
                  </label>
                ))}
                {options.groups.length === 0 && <p className="text-[14px] text-text-dim">등록된 그룹이 없습니다.</p>}
              </div>
            </fieldset>
            {!central && picked.size === 0 && <p role="status" className="text-[13px] text-text-dim">배정 없음 · 그룹 범위가 적용되는 조직에서는 그룹 자원에 접근할 수 없습니다.</p>}
          </>
        )}
      </div>
    </ModalShell>
  );
}
